import React, { useCallback } from 'react';
import { Filter as FilterIcon, ChevronDown, X, Plus } from 'lucide-react';
import type { DataSourceConfig, ReportFilter, FieldConfig } from '@/services/PagesServices/CustomReportBuilderService';
import { useReportBuilderState } from '../hooks/useReportBuilderState';

interface FiltersSectionProps {
  currentSource: DataSourceConfig | undefined;
}

const FilterOperatorOptions: React.FC<{ filterType: FieldConfig['filterType'] }> = ({ filterType }) => {
  if (filterType === 'select') {
    return (
      <>
        <option value="equals">Equals</option>
        <option value="in">In</option>
      </>
    );
  }
  if (filterType === 'date') {
    return (
      <>
        <option value="equals">Equals</option>
        <option value="greaterThan">After</option>
        <option value="lessThan">Before</option>
        <option value="between">Between</option>
      </>
    );
  }
  return (
    <>
      <option value="contains">Contains</option>
      <option value="equals">Equals</option>
      <option value="startsWith">Starts With</option>
    </>
  );
};

const FilterValueInput: React.FC<{
  filter: ReportFilter;
  fieldConfig: FieldConfig | undefined;
  onUpdate: (updated: ReportFilter) => void;
}> = ({ filter, fieldConfig, onUpdate }) => {
  if (fieldConfig?.filterType === 'select' && fieldConfig.filterOptions) {
    return (
      <select
        className="crb-filter-select"
        value={filter.value as string}
        onChange={(e) => onUpdate({ ...filter, value: e.target.value })}
      >
        <option value="">Select...</option>
        {fieldConfig.filterOptions.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  if (fieldConfig?.filterType === 'date') {
    return (
      <>
        <input
          type="date"
          className="crb-filter-input"
          value={filter.value as string}
          onChange={(e) => onUpdate({ ...filter, value: e.target.value })}
        />
        {filter.operator === 'between' && (
          <input
            type="date"
            className="crb-filter-input"
            value={filter.valueTo ?? ''}
            onChange={(e) => onUpdate({ ...filter, valueTo: e.target.value })}
          />
        )}
      </>
    );
  }

  return (
    <input
      type="text"
      className="crb-filter-input"
      placeholder="Value..."
      value={filter.value as string}
      onChange={(e) => onUpdate({ ...filter, value: e.target.value })}
    />
  );
};

const FiltersSection: React.FC<FiltersSectionProps> = ({ currentSource }) => {
  const expanded = useReportBuilderState(s => s.expandedSections.filters);
  const filters = useReportBuilderState(s => s.filters);
  const toggleSection = useReportBuilderState(s => s.toggleSection);
  const addFilter = useReportBuilderState(s => s.addFilter);
  const removeFilter = useReportBuilderState(s => s.removeFilter);
  const updateFilter = useReportBuilderState(s => s.updateFilter);
  const clearFilters = useReportBuilderState(s => s.clearFilters);

  const filterableFields = currentSource?.fields.filter(f => f.filterable) ?? [];

  const handleAddFilter = useCallback(() => {
    if (filterableFields.length === 0) return;
    const firstField = filterableFields[0];
    addFilter({
      field: firstField.key,
      operator: firstField.filterType === 'select' ? 'equals' : 'contains',
      value: '',
    });
  }, [filterableFields, addFilter]);

  return (
    <div className="crb-config-section">
      <div className="crb-config-header" onClick={() => toggleSection('filters')}>
        <div className="crb-config-header-left">
          <FilterIcon size={18} />
          <span className="crb-config-header-title">Filters</span>
          {filters.length > 0 && (
            <span className="crb-config-header-badge">{filters.length}</span>
          )}
        </div>
        <ChevronDown size={16} className={`crb-config-chevron ${expanded ? 'open' : ''}`} />
      </div>

      {expanded && (
        <div className="crb-config-body">
          <div className="crb-filter-list">
            {filters.map((filter, index) => {
              const fieldConfig = currentSource?.fields.find(f => f.key === filter.field);
              return (
                <div key={index} className="crb-filter-row">
                  <div className="crb-filter-row-header">
                    <span className="crb-filter-field-label">{fieldConfig?.label ?? filter.field}</span>
                    <button className="crb-filter-remove" onClick={() => removeFilter(index)}>
                      <X size={14} />
                    </button>
                  </div>
                  <div className="crb-filter-controls">
                    <select
                      className="crb-filter-select"
                      value={filter.field}
                      onChange={(e) => {
                        const newField = filterableFields.find(f => f.key === e.target.value);
                        updateFilter(index, {
                          ...filter,
                          field: e.target.value,
                          operator: newField?.filterType === 'select' ? 'equals' : 'contains',
                          value: '',
                        });
                      }}
                    >
                      {filterableFields.map(f => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                      ))}
                    </select>
                    <select
                      className="crb-filter-select"
                      value={filter.operator}
                      onChange={(e) => updateFilter(index, {
                        ...filter,
                        operator: e.target.value as ReportFilter['operator'],
                      })}
                    >
                      <FilterOperatorOptions filterType={fieldConfig?.filterType ?? 'text'} />
                    </select>
                  </div>
                  <div className="crb-filter-controls">
                    <FilterValueInput
                      filter={filter}
                      fieldConfig={fieldConfig}
                      onUpdate={(updated) => updateFilter(index, updated)}
                    />
                  </div>
                </div>
              );
            })}

            {filterableFields.length > 0 && (
              <button className="crb-add-filter-btn" onClick={handleAddFilter}>
                <Plus size={14} /> Add Filter
              </button>
            )}
            {filters.length > 0 && (
              <button
                className="crb-add-filter-btn"
                onClick={clearFilters}
                style={{ color: '#ef4444', borderColor: '#fecaca' }}
              >
                <X size={14} /> Clear All Filters
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(FiltersSection);
